import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { logger } from '../../helpers/logger.helper';

// Global tutorial flag key (not user-specific - shown before authentication)
const TUTORIAL_KEY = 'has_seen_tutorial';

// In-memory cache shared across all useTutorial instances in the same session.
// This prevents race conditions on iOS where multiple async AsyncStorage reads
// can return stale values between markTutorialAsSeen() and the next guard check.
let _cachedHasSeen: boolean | null = null;

/** Clears in-memory tutorial cache (e.g. between Vitest cases; one module instance otherwise leaks state). */
export function resetTutorialSeenCache(): void {
  _cachedHasSeen = null;
}

/**
 * Check if user has seen the tutorial (global flag, not user-specific)
 * Uses localStorage on Web and AsyncStorage on Mobile
 * @returns Promise<boolean> - true if user has seen tutorial, false otherwise
 */
export async function hasSeenTutorial(): Promise<boolean> {
  // Return in-memory cache immediately if available (avoids async race on iOS)
  if (_cachedHasSeen !== null) return _cachedHasSeen;

  try {
    // Web: use localStorage (with SSR safety check)
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const value = localStorage.getItem(TUTORIAL_KEY);
      _cachedHasSeen = value === 'true';
      return _cachedHasSeen;
    }

    // Mobile: use AsyncStorage
    const value = await AsyncStorage.getItem(TUTORIAL_KEY);
    _cachedHasSeen = value === 'true';
    return _cachedHasSeen;
  } catch (error) {
    if (__DEV__) {
      logger.warn('Failed to check tutorial status', { error });
    }
    return false;
  }
}

/**
 * Synchronously check if user has seen the tutorial (Web only)
 * Used for instant initialization in useState on Web
 * @returns boolean - true if user has seen tutorial, false otherwise
 */
export function hasSeenTutorialSync(): boolean {
  // Return in-memory cache immediately if available
  if (_cachedHasSeen !== null) return _cachedHasSeen;

  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    try {
      _cachedHasSeen = localStorage.getItem(TUTORIAL_KEY) === 'true';
      return _cachedHasSeen;
    } catch (error) {
      if (__DEV__) {
        logger.warn('Failed to check tutorial status (sync)', { error });
      }
      return false;
    }
  }
  return false; // Mobile always returns false (use async version)
}

/**
 * Mark tutorial as seen in storage
 * Uses localStorage on Web and AsyncStorage on Mobile
 * @returns Promise<void>
 */
export async function markTutorialAsSeen(): Promise<void> {
  // Update in-memory cache immediately so all useTutorial instances see the change
  // without waiting for the async storage write or doing another storage read.
  _cachedHasSeen = true;

  try {
    // Web: use localStorage (with SSR safety check)
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      localStorage.setItem(TUTORIAL_KEY, 'true');
      return;
    }

    // Mobile: use AsyncStorage
    await AsyncStorage.setItem(TUTORIAL_KEY, 'true');
  } catch (error) {
    if (__DEV__) {
      logger.warn('Failed to save tutorial status', { error });
    }
    // Silently fail - tutorial will show again on next app launch
  }
}

